import { CalendarOutlined } from "@ant-design/icons"
import { Button, DatePicker, Divider, Flex, Popover, Select } from "antd"
import { type RangePickerProps } from "antd/lib/date-picker"
import dayjs from "dayjs"
import React, { useState } from "react"

import { DATE_FORMAT } from "../../../../../../utils/date/date.ts"
import { LOG_DATE_PRESETS, matchLogDatePreset } from "./project-log-date-presets.ts"

const { RangePicker } = DatePicker

interface ProjectLogsDateRangeFilterProps {
  after: string | null
  before: string | null
  onChange: (patch: { after: string | null; before: string | null }) => void
}

/**
 * Collapses to one closed `Select` trigger, same shape as the shared
 * `DateRangeFilter`, but the popover offers quick presets first and only
 * drops into a raw `RangePicker` for "Custom Range" — this only changes how
 * `after`/`before` get chosen, never how `filterProjectLogs` reads them.
 */
export function ProjectLogsDateRangeFilter({
  after,
  before,
  onChange
}: ProjectLogsDateRangeFilterProps) {
  const [open, setOpen] = useState(false)
  const [customOpen, setCustomOpen] = useState(false)

  const activePreset = matchLogDatePreset(after, before)
  const hasRange = !!after && !!before

  const triggerLabel = activePreset
    ? LOG_DATE_PRESETS.find((preset) => preset.key === activePreset)?.label
    : hasRange
      ? `${dayjs(after).format(DATE_FORMAT)} - ${dayjs(before).format(DATE_FORMAT)}`
      : null

  const applyPreset = (key: string) => {
    const preset = LOG_DATE_PRESETS.find((p) => p.key === key)
    if (!preset) return
    const [start, end] = preset.range()
    onChange({ after: start, before: end })
    setCustomOpen(false)
    setOpen(false)
  }

  const onRangeChange: RangePickerProps["onChange"] = (_dates, dateStrings) => {
    onChange({ after: dateStrings?.[0] || null, before: dateStrings?.[1] || null })
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) setCustomOpen(false)
      }}
      trigger={"click"}
      content={
        <Flex vertical gap={2} className={"w-44"}>
          <Button
            type={!activePreset && !hasRange ? "primary" : "text"}
            size={"small"}
            className={"justify-start text-left"}
            onClick={() => {
              onChange({ after: null, before: null })
              setCustomOpen(false)
              setOpen(false)
            }}
          >
            All Time
          </Button>
          {LOG_DATE_PRESETS.map((preset) => (
            <Button
              key={preset.key}
              type={activePreset === preset.key ? "primary" : "text"}
              size={"small"}
              className={"justify-start text-left"}
              onClick={() => applyPreset(preset.key)}
            >
              {preset.label}
            </Button>
          ))}
          <Button
            type={!activePreset && hasRange ? "primary" : "text"}
            size={"small"}
            className={"justify-start text-left"}
            onClick={() => setCustomOpen(true)}
          >
            Custom Range
          </Button>

          {customOpen && (
            <>
              <Divider className={"my-1"} />
              <RangePicker
                autoFocus
                value={hasRange ? [dayjs(after), dayjs(before)] : undefined}
                onChange={onRangeChange}
                format={DATE_FORMAT}
              />
            </>
          )}
        </Flex>
      }
    >
      <Select
        allowClear
        aria-label={"Date Range"}
        open={false}
        prefix={<CalendarOutlined className={"text-gray-400"} />}
        value={triggerLabel}
        placeholder={"All time"}
        onClear={() => onChange({ after: null, before: null })}
        className={"w-full"}
      />
    </Popover>
  )
}
